import React from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

export type ScopeEntryDirtyState = 'new' | 'renamed' | 'moved' | 'retired' | 'restored';

export type ScopeTreeEntry = {
  scopeEntryKey: string;
  parentScopeEntryKey: string | null;
  title: string;
  sortOrder: number;
  isActive: boolean;
  /** Working-state marker carried by the editor. Absent means clean. */
  __dirty?: ScopeEntryDirtyState;
};

interface ScopeTreeViewProps {
  entries: ScopeTreeEntry[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /**
   * When supplied alongside onReparent, the tree becomes drag-enabled.
   * Both callbacks receive the moved entry's key and the target sibling
   * key it should be inserted just before. onReparent additionally tells
   * the caller which parent the entry should land under.
   */
  onReorder?: (movedKey: string, beforeSiblingKey: string) => void;
  onReparent?: (movedKey: string, newParentKey: string | null, beforeSiblingKey: string) => void;
}

interface TreeNode {
  entry: ScopeTreeEntry;
  children: TreeNode[];
}

interface FlatRow {
  entry: ScopeTreeEntry;
  depth: number;
  hasChildren: boolean;
  isOpen: boolean;
}

const buildTree = (entries: ScopeTreeEntry[]): TreeNode[] => {
  const byParent = new Map<string | null, ScopeTreeEntry[]>();
  for (const entry of entries) {
    const parent = entry.parentScopeEntryKey ?? null;
    const bucket = byParent.get(parent) ?? [];
    bucket.push(entry);
    byParent.set(parent, bucket);
  }
  for (const bucket of byParent.values()) {
    bucket.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  }
  const make = (entry: ScopeTreeEntry): TreeNode => ({
    entry,
    children: (byParent.get(entry.scopeEntryKey) ?? []).map(make)
  });
  return (byParent.get(null) ?? []).map(make);
};

const flattenForRender = (nodes: TreeNode[], expanded: Set<string>, depth = 0): FlatRow[] => {
  const flat: FlatRow[] = [];
  for (const node of nodes) {
    const isOpen = expanded.has(node.entry.scopeEntryKey);
    flat.push({ entry: node.entry, depth, hasChildren: node.children.length > 0, isOpen });
    if (isOpen) {
      flat.push(...flattenForRender(node.children, expanded, depth + 1));
    }
  }
  return flat;
};

const collectDescendants = (entries: ScopeTreeEntry[], rootKey: string): Set<string> => {
  const childrenByParent = new Map<string, ScopeTreeEntry[]>();
  for (const entry of entries) {
    if (!entry.parentScopeEntryKey) continue;
    const bucket = childrenByParent.get(entry.parentScopeEntryKey) ?? [];
    bucket.push(entry);
    childrenByParent.set(entry.parentScopeEntryKey, bucket);
  }
  const result = new Set<string>();
  const walk = (key: string): void => {
    const children = childrenByParent.get(key) ?? [];
    for (const child of children) {
      if (result.has(child.scopeEntryKey)) continue;
      result.add(child.scopeEntryKey);
      walk(child.scopeEntryKey);
    }
  };
  walk(rootKey);
  return result;
};

interface SortableRowProps {
  row: FlatRow;
  selectedKey: string | null;
  draggable: boolean;
  blockedKeys: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (key: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  row,
  selectedKey,
  draggable,
  blockedKeys,
  onToggle,
  onSelect
}) => {
  const sortable = useSortable({
    id: row.entry.scopeEntryKey,
    disabled: !draggable
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = sortable;
  const isSelected = selectedKey === row.entry.scopeEntryKey;
  const dirty = row.entry.__dirty;
  const dirtyClass = dirty ? ` is-dirty dirty-${dirty}` : '';
  const blocked = blockedKeys.has(row.entry.scopeEntryKey);
  const rowClass = [
    'scope-tree-row',
    isSelected ? 'is-selected' : '',
    row.entry.isActive ? '' : 'is-retired',
    isDragging ? 'is-dragging' : '',
    blocked ? 'is-blocked' : '',
    dirtyClass.trim()
  ].filter(Boolean).join(' ');
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    paddingLeft: 8 + row.depth * 14
  };
  return (
    <li ref={setNodeRef} style={{ listStyle: 'none' }}>
      <div className={rowClass} style={style}>
        {draggable && (
          <span
            className="drag-handle"
            aria-label="Drag to move"
            {...attributes}
            {...listeners}
          >
            ⋮⋮
          </span>
        )}
        <span
          role={row.hasChildren ? 'button' : 'presentation'}
          className={`twisty${row.hasChildren ? '' : ' is-leaf'}`}
          onClick={(event) => {
            if (!row.hasChildren) return;
            event.stopPropagation();
            onToggle(row.entry.scopeEntryKey);
          }}
          aria-label={row.hasChildren ? (row.isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {row.hasChildren ? (row.isOpen ? '▾' : '▸') : ''}
        </span>
        <button
          type="button"
          className="row-body"
          onClick={() => onSelect(row.entry.scopeEntryKey)}
        >
          <span className="title">{row.entry.title || '(untitled)'}</span>
          {!row.entry.isActive && <span className="retired-flag">retired</span>}
          {dirty === 'new' && <span className="dirty-flag flag-new">new</span>}
          {dirty === 'renamed' && <span className="dirty-flag flag-renamed">renamed</span>}
          {dirty === 'moved' && <span className="dirty-flag flag-moved">moved</span>}
          {dirty === 'retired' && <span className="dirty-flag flag-retired">retired</span>}
          {dirty === 'restored' && <span className="dirty-flag flag-restored">restored</span>}
        </button>
      </div>
    </li>
  );
};

/**
 * Scope tree view. Read-only when only onSelect is supplied; drag-enabled
 * for reorder + reparent when both onReorder and onReparent are passed.
 *
 * Drag rules:
 * - Dropping on a row in the same parent reorders the dragged entry to
 *   sit just before the target.
 * - Dropping on a row in a different parent reparents the dragged entry
 *   to that target's parent and places it just before the target.
 * - The dragged entry's descendants are not droppable (cycle prevention).
 *
 * Paths and depth are recomputed on the server when the revision is
 * published; the client does not predict them.
 */
export const ScopeTreeView: React.FC<ScopeTreeViewProps> = ({
  entries,
  selectedKey,
  onSelect,
  onReorder,
  onReparent
}) => {
  const tree = React.useMemo(() => buildTree(entries), [entries]);
  const draggable = Boolean(onReorder && onReparent);

  const [expanded, setExpanded] = React.useState<Set<string>>(() => {
    const set = new Set<string>();
    entries.forEach((entry) => {
      if (entry.parentScopeEntryKey == null) {
        set.add(entry.scopeEntryKey);
      }
    });
    return set;
  });
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const toggle = React.useCallback((key: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const flat = React.useMemo(() => flattenForRender(tree, expanded), [tree, expanded]);
  const flatIds = React.useMemo(() => flat.map((row) => row.entry.scopeEntryKey), [flat]);

  const blockedKeys = React.useMemo<Set<string>>(() => {
    if (!activeId) return new Set();
    const descendants = collectDescendants(entries, activeId);
    descendants.add(activeId);
    return descendants;
  }, [activeId, entries]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragStart = (event: DragStartEvent): void => {
    setActiveId(String(event.active.id));
  };

  const onDragEnd = (event: DragEndEvent): void => {
    setActiveId(null);
    if (!event.over || !onReorder || !onReparent) {
      return;
    }
    const movedKey = String(event.active.id);
    const targetKey = String(event.over.id);
    if (movedKey === targetKey) return;
    const moved = entries.find((entry) => entry.scopeEntryKey === movedKey);
    const target = entries.find((entry) => entry.scopeEntryKey === targetKey);
    if (!moved || !target) return;

    // Cycle prevention: target cannot be a descendant of the moved entry.
    if (collectDescendants(entries, movedKey).has(targetKey)) {
      return;
    }
    if (moved.parentScopeEntryKey === target.parentScopeEntryKey) {
      onReorder(movedKey, targetKey);
    } else {
      onReparent(movedKey, target.parentScopeEntryKey ?? null, targetKey);
    }
  };

  if (entries.length === 0) {
    return <div className="scopes-tree-empty">No entries in this Scope.</div>;
  }

  const activeRow = activeId ? flat.find((row) => row.entry.scopeEntryKey === activeId) : null;

  const inner = (
    <ul className="scope-tree-list">
      {flat.map((row) => (
        <SortableRow
          key={row.entry.scopeEntryKey}
          row={row}
          selectedKey={selectedKey}
          draggable={draggable}
          blockedKeys={blockedKeys}
          onToggle={toggle}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );

  if (!draggable) {
    return inner;
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
        {inner}
      </SortableContext>
      <DragOverlay>
        {activeRow ? (
          <div className="scope-tree-row is-drag-preview">
            <span className="drag-handle" aria-hidden="true">⋮⋮</span>
            <span className="title">{activeRow.entry.title || '(untitled)'}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ScopeTreeView;
